import React, { useEffect, useState, useMemo } from "react";
import { withTranslation } from "react-i18next";
import { Col, Row } from "reactstrap";
import { useSelector } from "react-redux";
import {
  Card,
  CardContent,
  Collapse,
  Dialog,
  DialogContent,
  DialogContentText,
  Stack,
  Typography,
  IconButton,
  Alert,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";

import AdminDiscussion from "common/AdminDiscussion";
import { CONSUMER, applicationStatusTypes } from "common/constant";
import CommentBox from "Components/Common/CommentBox";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";
import UtilityAddressView from "Components/Common/ViewApplicationDetailComponents/UtilityAddressView";
import CollapseComponent from "Components/Common/ViewApplicationDetailComponents/Collapse";
import CommentInput from "Components/Common/CommentInput";
import ApplicationApprovalActionButton from "Components/Common/ApplicationApprovalActionButton";
import { ApplicationApprovalTimelineChart } from "common/ApplicationApprovalTimelineChart";
import DeclarationView from "Components/Common/ViewApplicationDetailComponents/DeclarationView";
import PersonalDetailsView from "Components/Common/ViewApplicationDetailComponents/PersonalDetailsView";
import AuthorizedPersonDetailsView from "Components/Common/ViewApplicationDetailComponents/AuthorizedPersonDetailsView";
import DocumentsView from "Components/Common/ViewApplicationDetailComponents/DocumentsView";
import AccessoriesListView from "Components/Common/ViewApplicationDetailComponents/AccessoriesListView";
import ApplicationInfoView from "Components/Common/ViewApplicationDetailComponents/ApplicationInfoView";
import { getOptionValue } from "common/enums";
import DocumentItem from "pages/Admin/UserDocuments/DocumentItem";
import DcsDemandView from "Components/Common/ViewApplicationDetailComponents/DcsDemandView";
import SewageDemandView from "Components/Common/ViewApplicationDetailComponents/SewageDemandView";
import MeterRequisitionView from "Components/Common/ViewApplicationDetailComponents/MeterRequisitionView";
import WaterDemandView from "Components/Common/ViewApplicationDetailComponents/WaterDemandView";
import OtherApplicationExtraFieldsView from "Components/Common/ViewApplicationDetailComponents/OtherApplicationExtraFieldsView.js";
import Backdrop from "Components/Common/Backdrop";

import { isCommentInputVisible } from "utils";

import { useGetOtherApplicationDetailQuery } from "apis/otherApplicationDetails";
import {
  useLazyGetApplicationCommentQuery,
  usePostApplicationCommentMutation,
} from "apis/ApplicationRequestComment";

import { DialogHeader } from "./style";

const ViewOtherApplicationModal = ({
  isOpen,
  handleClose,
  viewApplicationQuery,
  t,
}) => {
  const [openDiscussion, setOpenDiscussion] = useState(true);
  const [documentToPreview, setDocumentToPreview] = useState();
  const {
    data: { data: applicationData = {} } = {},
    isLoading: isConsumerDataLoading,
    isFetching,
    isError,
  } = useGetOtherApplicationDetailQuery(viewApplicationQuery);
  const { roles } = useSelector((state) => state.Login);
  const [getAdminComments, { data: { data: commentData } = {} }] =
    useLazyGetApplicationCommentQuery(viewApplicationQuery.applicationId);
  const [addComment] = usePostApplicationCommentMutation();

  const isAdmin = useMemo(() => !roles.includes(CONSUMER), [roles]);

  const isDcsUtility = useMemo(
    () =>
      applicationData?.utilities[0] === getOptionValue("utilityOptions", "DCS"),
    [applicationData?.utilities[0]]
  );

  useEffect(() => {
    try {
      if (
        applicationData?.applicationStatus?.id &&
        applicationData.applicationStatus.id !== applicationStatusTypes["Saved"]
      ) {
        getAdminComments(viewApplicationQuery.applicationId);
      }
    } catch (error) {}
  }, [applicationData?.applicationStatus?.id]);

  useEffect(() => {
    if (commentData?.length >= 5) setOpenDiscussion((prev) => !prev);
  }, [commentData]);

  const addNewComment = async (comment) => {
    await addComment({
      applicationId: +viewApplicationQuery.applicationId,
      comment,
    });
  };

  const handleDiscussionCollapse = () => {
    setOpenDiscussion(!openDiscussion);
  };

  // Close modal if error occurs in consumer data api
  useEffect(() => {
    if (isError) {
      handleClose();
    }
  }, [isError]);

  if (isConsumerDataLoading || isFetching) {
    return <Backdrop open />;
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      scroll="paper"
    >
      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => setDocumentToPreview(null)}
          {...documentToPreview}
        />
      )}
      <DialogHeader>
        {t(`${applicationData?.applicationType?.name} Details`)}
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogHeader>
      <DialogContent>
        <DialogContentText variant="subtitle2" component="div">
          <div className="mt-2 ">
            {applicationData?.isApplicationByPass && (
              <Alert severity="info" className="mb-3">
                {t(
                  "This application directly moved to \"Process Complete\" after approval by bypassing Security Deposit."
                )}
              </Alert>
            )}
            {applicationData && (
              <Row className="d-flex mb-2 justify-content-center">
                <Col lg={6} md={10} className="mb-1">
                  <ApplicationInfoView
                    applicationInfo={{
                      imageUrl: applicationData.authorizedPerson?.imageUrl,
                      applicationNumber: applicationData.applicationNumber,
                      applicationTypeName: applicationData.applicationType.name,
                      submissionDate: applicationData.submissionDate,
                      demandEstimateUrl: applicationData.demandEstimateUrl,
                      demandEstimateReceiptUrl:
                        applicationData.demandEstimateReceiptUrl,
                      consumerNumber: applicationData.consumerNumber,
                      consumerName: applicationData.applicant?.applicantName,
                      disconnectionDate: applicationData.disconnectionDate,
                      utilities: applicationData.utilities,
                      salesOrder: applicationData.salesOrder,
                      serialNumber: applicationData.serialNumber,
                      applicationStatusName:
                        applicationData.applicationStatus?.name,
                    }}
                    isOtherUtility
                  />
                </Col>
              </Row>
            )}

            {!!applicationData?.applicant && (
              <PersonalDetailsView
                applicant={applicationData.applicant}
                applicationId={applicationData?.id}
                applicationStatusId={applicationData.applicationStatus.id}
                isOtherApplication
                isApplicationViewModal
              />
            )}

            {!!applicationData?.authorizedPerson && (
              <AuthorizedPersonDetailsView
                authorizedPerson={applicationData.authorizedPerson}
              />
            )}

            {(applicationData?.totalBuildupArea ||
              applicationData?.totalNumberOfOccupants ||
              applicationData?.expectedConnectionStartDate ||
              applicationData?.expectedConnectionEndDate) && (
              <OtherApplicationExtraFieldsView
                extraValues={{
                  tariff: applicationData.tariff,
                  applicationType: applicationData.applicationType,
                  totalBuildupArea: applicationData.totalBuildupArea,
                  totalNumberOfOccupants:
                    applicationData.totalNumberOfOccupants,
                  expectedConnectionStartDate:
                    applicationData.expectedConnectionStartDate,
                  expectedConnectionEndDate:
                    applicationData.expectedConnectionEndDate,
                  totalBuildupAreaUnit: applicationData.totalBuildupAreaUnit,
                  totalNumberOfOccupantsUnit:
                    applicationData.totalNumberOfOccupantsUnit,
                }}
              />
            )}

            {applicationData?.dcsDemand && (
              <>
                <DcsDemandView
                  dcsDemand={applicationData.dcsDemand}
                  applicationTypeId={applicationData.applicationType.id}
                />
                <MeterRequisitionView dcsDemand={applicationData.dcsDemand} />
              </>
            )}

            {applicationData?.sewageDemand && (
              <SewageDemandView
                applicationTypeId={applicationData.applicationType.id}
                sewageDemand={applicationData.sewageDemand}
              />
            )}

            {applicationData?.waterDemand && (
              <WaterDemandView
                applicationTypeId={applicationData.applicationType.id}
                waterDemand={applicationData.waterDemand}
              />
            )}

            {applicationData?.utilityAddress && (
              <UtilityAddressView
                utilityAddress={applicationData.utilityAddress}
                utilities={applicationData.utilities}
              />
            )}

            {!!applicationData?.declaration && (
              <DeclarationView
                declaration={applicationData.declaration}
                isPowerApplication={false}
              />
            )}

            {!!applicationData?.documents?.length && (
              <DocumentsView
                documents={applicationData.documents}
                isDcsUtility={isDcsUtility}
              />
            )}

            {!!applicationData?.additionalApplicationDocuments?.length && (
              <CollapseComponent title="Extra Documents" isExpandable>
                <div className="d-flex gap-3 flex-wrap">
                  {applicationData.additionalApplicationDocuments.map(
                    (document, index) => (
                      <DocumentItem document={document} key={index} />
                    )
                  )}
                </div>
              </CollapseComponent>
            )}

            {isAdmin && !!applicationData?.accessories?.length && (
              <AccessoriesListView accessories={applicationData.accessories} />
            )}
          </div>
          {applicationData?.applicationStatus?.name.toLowerCase() !==
            "saved" && (
            <>
              <div
                className="fs-4 fw-semibold mb-3 border-bottom"
                onClick={handleDiscussionCollapse}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <div>{t("Discussion")}</div>{" "}
                  <p>{openDiscussion ? <ExpandLess /> : <ExpandMore />}</p>
                </Stack>
              </div>
              <Collapse in={openDiscussion} timeout="auto" unmountOnExit>
                {!!applicationData?.applicationStatus?.name &&
                  isCommentInputVisible(
                    applicationData.applicationStatus.name,
                    roles,
                    !!commentData?.length
                  ) && (
                    <CommentInput
                      handleSubmit={addNewComment}
                      applicationId={applicationData?.id ?? ""}
                    />
                  )}
                {commentData?.length ? (
                  commentData.map(({ comment, createdAt, name }, index) => (
                    <CommentBox
                      comment={comment}
                      name={name}
                      createdAt={createdAt}
                      key={index}
                    />
                  ))
                ) : (
                  <Card variant="outlined" className="mt-2 p-0">
                    <CardContent className="pt-1">
                      <Typography color="GrayText" gutterBottom>
                        {t("No comment")}
                      </Typography>
                    </CardContent>
                  </Card>
                )}
              </Collapse>

              {isAdmin && (
                <AdminDiscussion
                  adminCommentsQuery={{
                    commentType: getOptionValue("adminComments", "Application"),
                    commentTypeId: viewApplicationQuery.applicationId,
                  }}
                />
              )}
              {isAdmin && !!applicationData?.id && (
                <ApplicationApprovalTimelineChart
                  applicationId={applicationData.id}
                  applicationStatus={applicationData.applicationStatus?.name}
                />
              )}
            </>
          )}
        </DialogContentText>
      </DialogContent>

      {applicationData &&
        applicationData.applicationStatus?.id !==
          applicationStatusTypes["Saved"] && (
          <ApplicationApprovalActionButton
            isOtherUtilityApplication
            applicationData={applicationData}
            handleClose={handleClose}
          />
        )}
    </Dialog>
  );
};

ViewOtherApplicationModal.propTypes = {
  title: PropTypes.string,
  handleClose: PropTypes.func,
  isOpen: PropTypes.bool,
  viewApplicationQuery: PropTypes.object,
  t: PropTypes.func,
};

export default withTranslation()(ViewOtherApplicationModal);
